import {isPlatformBrowser} from '@angular/common';
import {Inject, Injectable, OnDestroy, PLATFORM_ID} from '@angular/core';
import {Capacitor} from '@capacitor/core';
import {BehaviorSubject, fromEvent, Subscription} from 'rxjs';
import {distinctUntilChanged, map, startWith} from 'rxjs/operators';

@Injectable({providedIn: 'root'})
export class MobilePlatformPolicyService implements OnDestroy {
  /**
   * A browser window with DevTools open can become narrower than a tablet
   * breakpoint while it is still a desktop session. Keep the web experience
   * mobile-only at phone widths; native Capacitor always remains mobile.
   */
  public static readonly MOBILE_BREAKPOINT_PX = 767;

  private readonly isBrowser: boolean;
  private readonly mobileSubject = new BehaviorSubject<boolean>(false);
  private readonly reducedMotionSubject = new BehaviorSubject<boolean>(false);
  private readonly subscriptions = new Subscription();
  private readonly reducedMotionListener = (event: MediaQueryListEvent) => this.reducedMotionSubject.next(event.matches);
  private reducedMotionQuery: MediaQueryList | null = null;

  public readonly isMobileExperience$ = this.mobileSubject.asObservable().pipe(distinctUntilChanged());
  public readonly reducedMotion$ = this.reducedMotionSubject.asObservable().pipe(distinctUntilChanged());

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (!this.isBrowser) {
      return;
    }

    this.subscriptions.add(fromEvent(window, 'resize', {passive: true}).pipe(
      startWith(null),
      map(() => this.resolveMobileExperience()),
      distinctUntilChanged(),
    ).subscribe((active) => this.mobileSubject.next(active)));

    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.reducedMotionSubject.next(this.reducedMotionQuery.matches);
    this.reducedMotionQuery.addEventListener?.('change', this.reducedMotionListener);
  }

  public get isMobileExperience(): boolean {
    return this.mobileSubject.value;
  }

  public get prefersReducedMotion(): boolean {
    return this.reducedMotionSubject.value;
  }

  private resolveMobileExperience(): boolean {
    return Capacitor.isNativePlatform() || window.innerWidth <= MobilePlatformPolicyService.MOBILE_BREAKPOINT_PX;
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.reducedMotionQuery?.removeEventListener?.('change', this.reducedMotionListener);
  }
}
